import { useEffect } from 'react';
import { useLocation } from 'react-router';
import adminFaviconImage from '@/assets/admin-favicon.png';

/**
 * FaviconManager component that dynamically updates the favicon
 * based on the current route (admin vs. regular site)
 */
export const FaviconManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const updateFavicon = () => {
      // Check if we're on an admin route
      const isAdminRoute = location.pathname.startsWith('/admin-qa-management-portal');
      
      // Get existing favicon elements
      const existingLinks = document.querySelectorAll('link[rel*="icon"], link[rel*="apple-touch-icon"]');
      
      // Remove existing favicon links
      existingLinks.forEach(link => link.remove());
      
      if (isAdminRoute) {
        // Use the uploaded WAQ Admin favicon image
        const adminFaviconPng = document.createElement('link');
        adminFaviconPng.rel = 'icon';
        adminFaviconPng.type = 'image/png';
        adminFaviconPng.href = adminFaviconImage;
        document.head.appendChild(adminFaviconPng);
        
        // Add Apple Touch Icon for mobile
        const appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        appleTouchIcon.setAttribute('sizes', '32x32');
        appleTouchIcon.href = adminFaviconImage;
        document.head.appendChild(appleTouchIcon);
        
        // Update page title to indicate admin area
        document.title = 'WeAreQA Admin Portal';
      } else {
        /*
         * Files in public/, at the sizes browsers and search engines ask for,
         * rather than the 512px, 138 KB PNG this used for every size. Stable
         * URLs matter here too: Google's favicon fetcher reads these links from
         * the home page's HTML and wants a square that is a multiple of 48px,
         * hence the 96. /favicon.ico is served for the clients that request it
         * blind and never read the markup.
         */
        const icons: { rel: string; sizes: string; href: string; type?: string }[] = [
          { rel: 'icon', sizes: '96x96', href: '/favicon-96.png', type: 'image/png' },
          { rel: 'icon', sizes: '32x32', href: '/favicon-32.png', type: 'image/png' },
          { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
        ];
        for (const icon of icons) {
          const link = document.createElement('link');
          link.rel = icon.rel;
          link.setAttribute('sizes', icon.sizes);
          if (icon.type) link.type = icon.type;
          link.href = icon.href;
          document.head.appendChild(link);
        }

        /*
         * No title here. This used to reset it to a site-wide default on every
         * navigation, guarded by a list of routes that set their own -- a list
         * that had to be kept in step with the router by hand. The public
         * site's titles now come from one place, components/SeoHead.tsx. The
         * admin branch above keeps setting its own, because the panel has no
         * entry in the metadata registry and wants none.
         */
      }
    };

    updateFavicon();
  }, [location.pathname]);

  return null; // This component doesn't render anything
};